/**
 * Pointer hit testing for a canvas-only UI.
 *
 * The app draws no DOM, so there is nothing for the browser to hit-test. Each
 * renderer instead records the rectangle of every control it paints, in the
 * same logical units it draws in. The list is rebuilt every frame, so it can
 * never drift out of sync with what is on screen.
 */

/** Every control the pointer can reach. */
export type HitId =
  | 'theme'
  | 'sound'
  | 'volume'
  | 'music'
  | 'menu.name'
  | 'menu.mode.solo'
  | 'menu.mode.balance'
  | 'menu.mode.twin'
  | 'menu.start'
  | 'menu.scoreboard'
  | 'board.play'
  | 'board.back'
  | 'board.filter.all'
  | 'board.filter.solo'
  | 'board.filter.balance'
  | 'board.filter.twin'
  | 'game.start'
  | 'game.pause'
  | 'pause.resume'
  | 'pause.restart'
  | 'pause.menu'
  | 'over.restart'
  | 'over.scoreboard'
  | 'over.menu';

interface HitRegion {
  id: HitId;
  x: number;
  y: number;
  w: number;
  h: number;
}

export class HitList {
  private regions: HitRegion[] = [];

  /** Call once at the top of a frame, before anything is drawn. */
  clear(): void {
    this.regions.length = 0;
  }

  add(id: HitId, x: number, y: number, w: number, h: number): void {
    this.regions.push({ id, x, y, w, h });
  }

  /**
   * The control under a logical point, or `null`. Searched newest first, so a
   * control drawn on top of another — an overlay button over the field — wins.
   */
  at(x: number, y: number): HitId | null {
    for (let i = this.regions.length - 1; i >= 0; i--) {
      const r = this.regions[i];
      if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return r.id;
    }
    return null;
  }
}
